import { get, wrap } from 'object-path-immutable';
import isEqual from 'lodash.isequal';
import startCase from 'lodash.startcase';
import { LOCALIZED } from 'lib/block';
import { DEFAULT_LOCALE } from 'lib/localization';
import { capitalize } from 'utils/strings-handler';

/**
 * Helper function that augments `object-path-immutable.wrap` to conditionally set or assign values.
 * Creates a chainable API for modifying content while respecting whether values should be replaced or merged.
 *
 * @param {Object} content - The content object to wrap
 * @returns {Object} Wrapper object with chainable methods:
 *   - set: Always sets the value at path. Use when the new value is a whole updated object.
 *   - updateStyle: Sets or assigns value based on preserveOverrides. Use when adding a `theme` property to a style object.
 *   - deleteProperties: Deletes value if preserveOverrides is false. Use when removing a specific style property that may exist in the theme.
 *   - value: Returns the modified content
 */
export const augmentedWrap = content => {
  const baseWrap = wrap(content);
  const customWrap = {
    set: (path, value) => {
      baseWrap.set(path, value);
      return customWrap;
    },
    updateStyle: (path, value, preserveOverrides = false) => {
      const op = preserveOverrides ? baseWrap.assign : baseWrap.set;
      op(path, value);
      return customWrap;
    },
    deleteProperties: (paths, preserveOverrides = false) => {
      if (!preserveOverrides) {
        paths.forEach(path => {
          baseWrap.del(path);
        });
      }
      return customWrap;
    },
    value: baseWrap.value,
  };

  return customWrap;
};

/**
 * Helper function to make an `addTheme` function work with a localized block.
 * Applies theme changes to both the main content and any localized variations.
 *
 * @param {Object} content - The content object that may contain localized variations
 * @param {string} selectedVariant - The selected variant to apply to the content
 * @param {boolean} preserveOverrides - Whether to preserve existing styles as overrides
 * @param {Function} contentThemer - `addTheme` function that applies theme changes to a single content block
 * @returns {Object} Content with theme applied to main content and variations or an unlocalized block
 */
export const applyThemeInLocalizedBlock = (
  content,
  selectedVariant,
  preserveOverrides,
  contentThemer
) => {
  if (content.blockType !== LOCALIZED) {
    return contentThemer(content, selectedVariant, preserveOverrides);
  }

  return {
    ...content,
    content: contentThemer(content.content, selectedVariant, preserveOverrides),
    variations: Object.fromEntries(
      Object.entries(content.variations).map(([key, variationContent]) => [
        key,
        contentThemer(variationContent, selectedVariant, preserveOverrides),
      ])
    ),
  };
};

/**
 * Helper function to make an `overrides` function work with a localized block.
 *
 * @param {Object} args - The parameters object
 * @param {Object} args.compiledTheme - The compiled theme object
 * @param {Object} args.content - The content object that may contain localized variations
 * @param {string} args.path - The path to the content within the object
 * @param {string} args.pathName - The name of the path for labeling purposes
 * @param {Function} overrideCalculator - `overrides` function for a single content block. Expects an object with keys: compiledTheme, content, path, pathName
 * @returns {Array} Array of override objects with paths and locale IDs
 */
export const calculateOverridePathsInLocalizedBlock = (
  args,
  overrideCalculator
) => {
  const { content, path } = args;
  const pathContent = get(content, path);
  if (pathContent?.blockType !== LOCALIZED) {
    return overrideCalculator(args);
  }

  const pathPrefix = path ? `${path}.` : '';
  const localizedContentPaths = [
    {
      localizedPath: `${pathPrefix}content`,
      localeId: DEFAULT_LOCALE.id,
    },
    ...Object.keys(pathContent.variations).map(localeId => ({
      localizedPath: `${pathPrefix}variations.${localeId}`,
      localeId,
    })),
  ];

  // Get overrides for each localized content path, and annotate with the locale id
  const overrides = localizedContentPaths.flatMap(
    ({ localizedPath, localeId }) => {
      const localizedOverrides = overrideCalculator({
        ...args,
        path: localizedPath,
      });

      return localizedOverrides.map(override => ({
        ...override,
        localeIds: [localeId],
      }));
    }
  );

  // Group nested overrides by key, merging path and localeId arrays
  return Object.values(
    overrides.reduce((acc, override) => {
      const { key, paths, localeIds, ...rest } = override;
      const { paths: existingPaths = [], localeIds: existingLocaleIds = [] } =
        acc[key] ?? {};

      return {
        ...acc,
        [key]: {
          ...rest,
          key,
          paths: [...existingPaths, ...paths],
          localeIds: [...existingLocaleIds, ...localeIds],
        },
      };
    }, {})
  );
};

const normalizedValue = value => {
  // color hex values are case-insensitive
  if (typeof value === 'object' && value.light?.startsWith('#')) {
    return {
      ...value,
      light: value.light.toLowerCase(),
      ...(value.dark && { dark: value.dark.toLowerCase() }),
    };
  }

  return value;
};

/**
 * Remove theme styles (ie styles that match the theme values) from a style object
 * @param {Object} compiledTheme - Theme object
 * @param {Object} object - Style object
 * @returns {Object} Style object with theme styles stripped
 */
export const untheme = (compiledTheme, object) => {
  if (!object.theme) {
    return { ...object };
  }

  const themeStyles = compiledTheme.content?.components?.[object.theme];
  if (!themeStyles) {
    return { ...object };
  }

  return Object.entries(object).reduce((acc, [key, value]) => {
    // Keep values that differ from theme
    const styleModifiesTheme = !isEqual(
      normalizedValue(themeStyles[key]),
      normalizedValue(value)
    );
    // Keep values that are not 0 or keep values that are 0 if the theme value is set (otherwise, we can skip 0 values so they don't show as overriding the theme)
    const shouldKeepValue = value !== 0 || themeStyles[key] !== undefined;
    if (key === 'theme' || (styleModifiesTheme && shouldKeepValue)) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const buildPaths = (pathBuilder, properties) => {
  const targetLengthArray = [
    ...Array.from({ length: pathBuilder.count }).keys(),
  ];
  return targetLengthArray.flatMap(i =>
    properties.flatMap(prop => {
      const paths = pathBuilder.fn(i);
      return (Array.isArray(paths) ? paths : [paths]).map(
        path => `${path}.${prop}`
      );
    })
  );
};

export const labelForOverrideKey = key => capitalize(startCase(key));

/**
 * Returns an array of override paths for a given content object based on the theme
 * @param {Object} compiledTheme - Compiled theme object
 * @param {Object} content - Content object
 * @param {string} path - Path to the style object
 * @param {string} pathName - Label prefix for the paths
 * @param {string[]} ignore - Array of keys to ignore (denylist)
 * @param {string[]} allow - Array of keys to allow (allowlist). If set, only keys in the allowlist will be included
 * @param {Object} group - Object keyed by property name with an array of properties to be grouped with the key property
 * @param {Object} pathBuilder - For repeated content (survey options): Object of path builder
 * @param {number} pathBuilder.count - Number of times to repeat the path
 * @param {function} pathBuilder.fn - Function to build the path using the count
 * @returns {Array} paths - Array of override paths with unique key, UI label, and array of path(s) to update
 */
export const calculateOverridePaths = ({
  compiledTheme,
  content,
  path,
  pathName,
  ignore = [],
  allow,
  group,
  pathBuilder,
}) => {
  const style = get(content, path);

  if (!style) {
    return [];
  }

  return (
    Object.keys(untheme(compiledTheme, style))
      // filter out theme, keys to ignore, and keys that aren't specifically allowed (if an allowlist is provided)
      .filter(
        key =>
          key !== 'theme' &&
          !ignore.includes(key) &&
          (!allow || allow.includes(key))
      )
      .map(property => {
        const keyPrefix = pathName ? `${pathName}.` : '';
        const pathPrefix = path !== '' ? `${path}.` : '';
        const properties = [property, ...(group?.[property] ?? [])];
        const paths = pathBuilder
          ? buildPaths(pathBuilder, properties)
          : properties.map(prop => `${pathPrefix}${prop}`);

        return {
          key: `${keyPrefix}${property}`,
          label: labelForOverrideKey(`${keyPrefix}${property}`),
          paths,
        };
      })
  );
};

export const transformValueFromThemeFormat = value => {
  if (typeof value === 'string' && value.endsWith('px')) {
    return Number.parseFloat(value);
  }

  if (value === '100%') {
    return -1;
  }

  return value;
};

export const transformValueToThemeFormat = value => {
  if (value === -1) {
    return '100%';
  }

  if (typeof value === 'number') {
    return `${value}px`;
  }

  return value;
};

export const styleToThemeStyle = ({ style, ignore = [] }) => {
  return Object.entries(style).reduce((acc, [key, value]) => {
    if (key !== 'theme' && !ignore.includes(key)) {
      acc[key] = transformValueToThemeFormat(value);
    }

    // special case where a raw theme style might have fontFamily and fontWeight,
    // but if we're specifically setting fontName, we want to remove the other two
    if (key === 'fontName') {
      acc.fontFamily = undefined;
      acc.fontWeight = undefined;
    }

    return acc;
  }, {});
};
